import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AxiosError } from "axios";

import { analyseInserat, type Inserat } from "@/api/inserate";
import { Button } from "@/components/ui/button";

/** Per-row "Analyse" button that triggers POST /api/inserate/{key}/analyse. */
export function AnalyseCell({ inserat }: { inserat: Inserat }) {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => analyseInserat(inserat.key, inserat.source_url),
    onSuccess: (data) => {
      const verb = data.is_new ? "angelegt" : "aktualisiert";
      toast.success(`Objekt ${verb}: ${data.slug}`, {
        description: `objekte/${data.slug}/`,
      });
      void qc.invalidateQueries({ queryKey: ["inserate"] });
      void qc.invalidateQueries({ queryKey: ["objekte"] });
    },
    onError: (err) => {
      // Surface the FastAPI 502 detail if available — usually contains the
      // anti-bot hint or the underlying scrape exception.
      const detail =
        err instanceof AxiosError
          ? (err.response?.data as { detail?: string } | undefined)?.detail ??
            err.message
          : err instanceof Error
            ? err.message
            : "Unbekannter Fehler";
      toast.error("Analyse fehlgeschlagen", { description: detail });
    },
  });

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      data-testid={`analyse-btn-${inserat.key}`}
      aria-label={`Analyse starten für ${inserat.title ?? inserat.key}`}
    >
      {mutation.isPending ? "läuft …" : "Analyse"}
    </Button>
  );
}
